import { observer } from "mobx-react-lite";
import PaymentGatewayController from "./payment.gateway.controller";
import React, { FormEvent, useEffect } from "react";
import i from "../../../translations/i";
import { Titles } from "../../../layouts/Titles";
import {
  Button,
  Col,
  Divider,
  Input,
  Select,
  Row,
  Segmented,
  Space,
  Table,
} from "antd";
import { useParams } from "react-router-dom";
import {
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  KeyOutlined,
  FileTextOutlined,
  CreditCardOutlined,
} from '@ant-design/icons'
import { SectionBadge, FieldBlock } from "../../../components/SectionCard";
import { SwitchBlock } from "../../../components/SwitchBlock";

const GRADIENT_NEUTRAL = 'linear-gradient(135deg, #3f3d56, #63607f)'
const GRADIENT_PRIMARY = 'linear-gradient(135deg, var(--primary-color), #9186e0)'

export const PaymentGateway = observer(() => {

  let { id }: any = useParams()

  useEffect(() => {
    PaymentGatewayController.getApps()
    PaymentGatewayController.getOne(id)
  }, [])

  const item = PaymentGatewayController.item
  const isYookassa = item.app_setting === null

  return(
    <>
      <form onSubmit={(e: FormEvent) => PaymentGatewayController.save(e)}>
        <Titles
          title={i.t('def:Payment gateway')}
          buttons={[
            { title: i.t('def:actions.backList'), type: 'dashed', href: `/settings/payment_gateway` },
            { title: i.t('def:actions.save'), type: 'primary', htmlType: 'submit' }
          ]}
          breadcrumb={[
            {title: i.t('def:settings'), href: '/settings'},
            { title: i.t('def:Payment gateway list'), href: '/settings/payment_gateway' },
            { title: i.t('def:Payment gateway') },
          ]}
        />

        <Divider />

        <Row gutter={[20, 20]}>
          <Col span={24}>
            <div className="order__block" style={{ width: '100%', height: 'auto', alignSelf: 'flex-start' }}>
              <SectionBadge icon={<SettingOutlined />} text={i.t('def:Payment gateway')} background={GRADIENT_NEUTRAL} />
              <Row gutter={[16, 16]} style={{ width: '100%', margin: 0 }}>
                <Col span={12} style={{ paddingLeft: 0 }}>
                  <FieldBlock label={i.t('def:name')}>
                    <Input
                      name="title"
                      placeholder={i.t('def:name')}
                      value={item.label}
                      onChange={(e) => item.label = e.target.value}
                      required
                    />
                  </FieldBlock>
                </Col>

                <Col span={12} style={{ paddingRight: 0 }}>
                  <FieldBlock label={i.t('paymentGateway:selectApp')}>
                    <Select
                      style={{ width: `100%` }}
                      value={item.app_setting}
                      onChange={(e) => item.app_setting = e}
                      options={[
                        { label: 'Юkassa', value: null },
                        ...PaymentGatewayController.apps.map((el) => ({
                          label: el.application.config.name,
                          value: el.id,
                        })),
                      ]}
                    />
                  </FieldBlock>
                </Col>
              </Row>
            </div>
          </Col>

          {isYookassa &&
            <>
              <Col span={24} xl={12}>
                <div className="order__block" style={{ width: '100%', height: 'auto', alignSelf: 'flex-start' }}>
                  <SectionBadge icon={<KeyOutlined />} text={i.t('paymentGateway:yookassaAccess')} background={GRADIENT_NEUTRAL} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
                    <FieldBlock label={i.t('def:Yookassa shop_id')}>
                      <Input
                        placeholder={i.t('def:Yookassa shop_id')}
                        value={item.shop_id}
                        onChange={(e) => item.shop_id = e.target.value}
                        required
                      />
                    </FieldBlock>

                    <FieldBlock label={i.t('def:Yookassa secret_key')}>
                      <Input
                        placeholder={i.t('def:Yookassa secret_key')}
                        value={item.secret_key}
                        onChange={(e) => item.secret_key = e.target.value}
                        required
                      />
                    </FieldBlock>

                    <FieldBlock label={i.t('def:Yookassa gateway_id')}>
                      <Input
                        placeholder={i.t('def:Yookassa gateway_id')}
                        value={item.gateway_id}
                        onChange={(e) => item.gateway_id = e.target.value}
                        required
                      />
                    </FieldBlock>
                  </div>
                </div>

                <div style={{ height: 20 }} />

                <div className="order__block" style={{ width: '100%', height: 'auto', alignSelf: 'flex-start' }}>
                  <SectionBadge icon={<FileTextOutlined />} text={i.t('paymentGateway:receiptAndTaxes')} background={GRADIENT_NEUTRAL} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
                    <FieldBlock label={i.t('def:Yookassa tax_system_code')}>
                      <Select
                        value={Number(item.tax_system_code)}
                        options={[
                          { label: i.t('paymentGateway:taxSystem.general'), value: 1 },
                          { label: i.t('paymentGateway:taxSystem.usnIncome'), value: 2 },
                          { label: i.t('paymentGateway:taxSystem.usnIncomeMinusExpenses'), value: 3 },
                          { label: i.t('paymentGateway:taxSystem.envd'), value: 4 },
                          { label: i.t('paymentGateway:taxSystem.esn'), value: 5 },
                          { label: i.t('paymentGateway:taxSystem.patent'), value: 6 },
                        ]}
                        onChange={(e: any) => item.tax_system_code = e}
                        style={{width: `100%`}}
                      />
                    </FieldBlock>

                    <FieldBlock label={i.t('def:Yookassa vat_code')}>
                      <Select
                        value={Number(item.vat_code)}
                        options={[
                          { label: i.t('paymentGateway:vat.none'), value: 1 },
                          { label: i.t('paymentGateway:vat.rate0'), value: 2 },
                          { label: i.t('paymentGateway:vat.rate10'), value: 3 },
                          { label: i.t('paymentGateway:vat.receiptRate20'), value: 4 },
                          { label: i.t('paymentGateway:vat.receiptCalc10_110'), value: 5 },
                          { label: i.t('paymentGateway:vat.receiptCalc20_120'), value: 6 },
                          { label: i.t('paymentGateway:vat.rate5'), value: 7 },
                          { label: i.t('paymentGateway:vat.rate7'), value: 8 },
                          { label: i.t('paymentGateway:vat.calc5_105'), value: 9 },
                          { label: i.t('paymentGateway:vat.calc7_107'), value: 10 },
                          { label: i.t('paymentGateway:vat.rate22'), value: 11 },
                          { label: i.t('paymentGateway:vat.calc22_122'), value: 12 },
                        ]}
                        onChange={(e: any) => item.vat_code = e}
                        style={{width: `100%`}}
                      />
                    </FieldBlock>

                    <FieldBlock label={i.t('paymentGateway:paymentModeLabel')}>
                      <Segmented
                        style={{ width: `100%` }}
                        block
                        options={[
                          { label: i.t('paymentGateway:fullPrepayment'), value: 'full_prepayment' },
                          { label: i.t('paymentGateway:fullPayment'), value: 'full_payment' },
                        ]}
                        value={item.payment_mode}
                        onChange={(e: any) => item.payment_mode = e}
                      />
                    </FieldBlock>
                  </div>
                </div>
              </Col>

              <Col span={24} xl={12}>
                <div className="order__block" style={{ width: '100%', height: 'auto', alignSelf: 'flex-start' }}>
                  <SectionBadge icon={<CreditCardOutlined />} text={i.t('paymentGateway:paymentMethodsAndAutoWriteoff')} background={GRADIENT_PRIMARY} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
                    <SwitchBlock array={[
                      { label: i.t('def:Yookassa card_online'), click: (e: any) => item.card_online = e, value: item.card_online },
                      { label: i.t('def:Yookassa save_card'), click: (e: any) => item.save_card = e, value: item.save_card, disabled: !item.card_online },

                      { label: i.t('def:Yookassa sbp'), click: (e: any) => item.sbp = e, value: item.sbp, block: 2 },
                      { label: i.t('def:Yookassa save_sbp'), click: (e: any) => item.save_sbp = e, value: item.save_sbp, disabled: !item.sbp },

                      { label: i.t('def:Yookassa sberpay'), click: (e: any) => item.sberpay = e, value: item.sberpay },
                      { label: i.t('def:Yookassa save_sberpay'), click: (e: any) => item.save_sberpay = e, value: item.save_sberpay, disabled: !item.sberpay },

                      { label: i.t('def:Yookassa mirpay'), click: (e: any) => item.mirpay = e, value: item.mirpay },
                      { label: i.t('def:Yookassa save_mirpay'), click: (e: any) => item.save_mirpay = e, value: item.save_mirpay, disabled: !item.mirpay },

                      { label: i.t('def:Yookassa tinkoff_pay'), click: (e: any) => item.tinkoff_pay = e, value: item.tinkoff_pay },
                      { label: i.t('def:Yookassa save_tinkoff_pay'), click: (e: any) => item.save_tinkoff_pay = e, value: item.save_tinkoff_pay, disabled: !item.tinkoff_pay },

                      { label: i.t('def:Yookassa alfa_pay'), click: (e: any) => item.alfa_pay = e, value: item.alfa_pay },
                      { label: i.t('def:Yookassa save_alfa_pay'), click: (e: any) => item.save_alfa_pay = e, value: item.save_alfa_pay, disabled: !item.alfa_pay },

                      { label: i.t('paymentGateway:installments'), click: (e: any) => item.sber_bnpl = e, value: item.sber_bnpl },

                      { label: i.t('def:Yookassa smart_payment'), click: (e: any) => item.smart_payment = e, value: item.smart_payment },
                    ]} />
                  </div>
                </div>
              </Col>
            </>
          }
        </Row>
      </form>
    </>
  )
})

export const PaymentGatewayList = observer(() => {

  useEffect(() => {
    PaymentGatewayController.getList()
  }, [])

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: i.t('def:Label'),
      dataIndex: 'label',
      key: 'label'
    },
    {
      title: i.t('def:Yookassa shop_id'),
      dataIndex: 'shop_id',
      key: 'shop_id'
    },
    {
      title: i.t('orders:actions'),
      key: 'action',
      render: (item: any) => (
        <>
          <Space>
            <Button
              type="dashed"
              href={`/settings/payment_gateway/${item.id}`}
              icon={<EditOutlined />}
              shape="circle"
            />

            <Button
              type="dashed"
              onClick={() => PaymentGatewayController.destroy(item.id)}
              icon={<DeleteOutlined />}
              shape="circle"
              danger
            />
          </Space>
        </>
      )
    }
  ]

  return(
    <>
      <Titles
        title={i.t('def:Payment gateway list')}
        buttons={[
          { title: i.t('def:actions.create'), type: 'primary', href: `/settings/payment_gateway/0` }
        ]}
        breadcrumb={[
          {title: i.t('def:settings'), href: '/settings'},
          { title: i.t('def:Payment gateway list') },
        ]}
      />

      <Table
        rowKey="id"
        columns={columns}
        dataSource={PaymentGatewayController.list}
        pagination={false}
      />

    </>
  )
})